/**
 * Calidad: la hoja de RE-Q-03 como borrador POR SILO, y su bajada al payload
 * `muestra.crear` (docs/esquema.md §4.1, §5.2.12–13). Del boceto «Calidad ·
 * teléfono» (`../Perejil/public/poc/produccion.html`, gerenta 7/9): la hoja se
 * reparte en pantallas por grupo, no hay orden obligatorio, y una hoja puede
 * quedar a medias mientras la humedad tarda sus ocho minutos.
 *
 * Los números se guardan como TEXTO con coma mientras se tipean («24,5»): la
 * conversión a número vive solo acá, al proponer y al armar el payload. El
 * árbol que propone la letra es el mismo del Worker (`./calidad`), con la regla
 * vigente que bajó en los catálogos.
 */

import { motivoObligatorio, proponerLetra, sumaDeTamices, type DefectoObservado, type LetraCalidad, type Propuesta, type ReglaCalidad } from './calidad';
import { ALGORITMOS_CONOCIDOS } from './calidad';
import type { MutacionPendiente } from './produccion';
import { HORAS_PARA_CORREGIR, instanteLocal, msDeInstanteLocal } from './tiempo';

export type SiNo = 0 | 1;
export type Grado = 'P' | 'B' | 'M';
export type PantallaHoja = 'hoja' | 'quien' | 'medir' | 'palos' | 'tamices' | 'extranios' | 'adventicios' | 'resultado' | 'nota';
export type CampoNumerico = 'densidad' | 'humedad' | 'tamiz_sobre_3_5' | 'tamiz_sobre_6' | 'tamiz_sobre_40' | 'tamiz_bajo_40';

/** Cuántos caracteres admite un campo numérico: «12,5» cabe, «100,00» también. */
export const MAX_CARACTERES_NUMERO = 6;
/** Lo que el servidor acepta en motivo y notas (`MAX_TEXTO`). */
export const MAX_TEXTO_MUESTRA = 500;
/** El árbol que esta app sabe evaluar; una regla con otro exige actualizar la app. */

/** Lo que el catálogo trae de cada silo que espera muestra (§5.5, `llenados_para_muestra`). */
export interface SiloParaMuestra {
	llenado_id: string;
	campania_id: string;
	silo_fisico_id: string;
	lote_campo_id: string;
	precinto: string;
	cerrado_en: string;
	neto_g: number;
}

export interface DefectoDeCatalogo {
	id: string;
	grupo: 'material_extranio' | 'adventicio';
	nombre: string;
	orden: number;
	activo: number;
}

/**
 * La hoja de un silo. `capture_id` ES el `llenado_id`: un borrador por silo, y
 * abrir la hoja de un silo es buscar por su clave — cambiar de silo no arrastra
 * lo cargado en otro (revisión del 7/9).
 */
export interface BorradorMuestra {
	tipo: 'muestra';
	capture_id: string;
	mutacion_id: string;
	muestra_id: string;
	llenado_id: string;
	campania_id: string;
	/** La foto del silo al abrir la hoja: lo que la pantalla muestra, congelado. */
	silo_numero: string;
	precinto: string;
	lote: string;
	neto_g: number;
	cerrado_en: string;
	/** La analista (una sola; la grilla de verde habla en listas). */
	personas: string[];
	densidad: string;
	humedad: string;
	/** `null` = propuesto en cero (r. 24): se guarda 0 si nadie lo toca. */
	tamiz_sobre_3_5: string | null;
	tamiz_sobre_6: string;
	tamiz_sobre_40: string;
	tamiz_bajo_40: string;
	palos: SiNo | null;
	contaminacion_grave: SiNo | null;
	/** Solo los presentes: `defecto_id` → grado. Ausente = N. */
	defectos: Record<string, Grado>;
	/** «Otros, cuál»: el texto cuando se marca el defecto «otro» (gestión lo normaliza). */
	defecto_otro: string | null;
	/** La letra que la analista fijó a mano; `null` mientras siga la propuesta. */
	letra_manual: LetraCalidad | null;
	/**
	 * v6 (§12.4): una nota para el silo, opcional; viaja como `nota_llenado.crear` junto con la
	 * muestra. Opcionales en el tipo: una hoja guardada antes no los trae (se generan al guardar).
	 */
	nota_texto?: string | null;
	nota_id?: string;
	nota_mutacion_id?: string;
	motivo: string;
	nota: string | null;
	/** Dónde quedó la hoja y qué campo tenía el teclado: retomar vuelve ahí. */
	pantalla: PantallaHoja;
	campo: CampoNumerico | null;
	/** Cuándo se abrió la hoja: la muestra se toma cuando el silo llega al laboratorio. */
	tomada_ms: number;
	/**
	 * La versión de la regla con la que la analista miró el resultado (y fijó la letra,
	 * si la fijó). Si al guardar la vigente es otra, el resultado se revisa de nuevo.
	 */
	regla_version: number | null;
	actualizado_en: number;
	/**
	 * §12.18: la hoja corrige una muestra ya guardada (la base, como bajó o como quedó en este teléfono); sin esto es un
	 * alta. Su `capture_id` es propio, no el silo: no choca con el alta del mismo silo ni con `capturas_consumidas`.
	 */
	corrige?: HojaParaCorregir;
}

export const CAMPOS_NUMERICOS: CampoNumerico[] = ['densidad', 'humedad', 'tamiz_sobre_3_5', 'tamiz_sobre_6', 'tamiz_sobre_40', 'tamiz_bajo_40'];
export const CAMPOS_MEDIR: CampoNumerico[] = ['densidad', 'humedad'];
export const CAMPOS_TAMICES: CampoNumerico[] = ['tamiz_sobre_3_5', 'tamiz_sobre_6', 'tamiz_sobre_40', 'tamiz_bajo_40'];

export const NOMBRE_CAMPO: Record<CampoNumerico, string> = {
	densidad: 'Densidad',
	humedad: 'Humedad',
	tamiz_sobre_3_5: 'Sobre 3,5',
	tamiz_sobre_6: 'Sobre 6',
	tamiz_sobre_40: 'Sobre 40',
	tamiz_bajo_40: 'Bajo 40 · polvo',
};

export const UNIDAD_CAMPO: Record<CampoNumerico, string> = {
	densidad: '',
	humedad: '%',
	tamiz_sobre_3_5: 'g',
	tamiz_sobre_6: 'g',
	tamiz_sobre_40: 'g',
	tamiz_bajo_40: 'g',
};

/** Los rangos que el servidor exige (`validarMuestra`): atajarlos acá evita un rechazo tardío. */
const RANGO_CAMPO: Record<CampoNumerico, [number, number]> = {
	densidad: [0.001, 1000],
	humedad: [0, 100],
	tamiz_sobre_3_5: [0, 100],
	tamiz_sobre_6: [0, 100],
	tamiz_sobre_40: [0, 100],
	tamiz_bajo_40: [0, 100],
};

// ── Números tipeados ──────────────────────────────────────────────────────

/** «24,5» → 24.5; vacío o a medias («12,») → null. Acepta punto por si el teclado del sistema lo mete. */
export function numeroDe(texto: string | null): number | null {
	if (texto === null) return null;
	const limpio = texto.trim().replace(',', '.');
	if (!/^\d+(\.\d+)?$/.test(limpio)) return null;
	return Number(limpio);
}

/** El texto efectivo de un campo: el propuesto «0» cuenta como cargado. */
export function textoDe(b: BorradorMuestra, campo: CampoNumerico): string {
	const valor = b[campo];
	return valor === null ? '0' : valor;
}

export function propuestoEnCero(b: BorradorMuestra, campo: CampoNumerico): boolean {
	return campo === 'tamiz_sobre_3_5' && b.tamiz_sobre_3_5 === null;
}

export function valorDe(b: BorradorMuestra, campo: CampoNumerico): number | null {
	return numeroDe(textoDe(b, campo));
}

/** Un dígito o la coma sobre el texto de un campo, con el tope de caracteres y una sola coma. */
export function tipearEn(actual: string, tecla: string): string {
	if (tecla === ',') return actual.includes(',') || actual === '' ? actual : actual.length < MAX_CARACTERES_NUMERO ? `${actual},` : actual;
	if (actual.length >= MAX_CARACTERES_NUMERO) return actual;
	// Un cero a la izquierda se reemplaza: «0» + «5» es «5», no «05».
	if (actual === '0') return tecla;
	return `${actual}${tecla}`;
}

/** Cargado y dentro del rango que el servidor acepta. */
export function campoAceptable(b: BorradorMuestra, campo: CampoNumerico): boolean {
	const valor = valorDe(b, campo);
	if (valor === null) return false;
	const [min, max] = RANGO_CAMPO[campo];
	return valor >= min && valor <= max;
}

// ── Qué falta, qué propone la regla, qué se guarda ────────────────────────

/**
 * v6 (§12.9): la humedad vacía no frena la hoja —a veces se embolsa antes de tenerla—. Se guarda sin
 * ella, la letra queda provisoria y se completa después desde la lista «Falta humedad».
 */
export function sinHumedad(b: Pick<BorradorMuestra, 'humedad'>): boolean {
	return (b.humedad ?? '') === '';
}

/** Los cuatro tamices son obligatorios: la hoja entera, no solo lo que la propuesta necesita (boceto). */
export function faltantes(b: BorradorMuestra): string[] {
	const faltan: string[] = [];
	if (b.personas.length === 0) faltan.push('quién analiza');
	for (const campo of CAMPOS_MEDIR) if (!(campo === 'humedad' && sinHumedad(b)) && !campoAceptable(b, campo)) faltan.push(NOMBRE_CAMPO[campo].toLowerCase());
	if (b.palos === null) faltan.push('palos');
	if (b.contaminacion_grave === null) faltan.push('contaminación grave');
	for (const campo of CAMPOS_TAMICES) if (!campoAceptable(b, campo)) faltan.push(NOMBRE_CAMPO[campo].toLowerCase());
	return faltan;
}

/** Los defectos marcados, con su grupo del catálogo (los que el catálogo ya no tiene, sin grupo, no se evalúan). */
export function defectosObservados(b: BorradorMuestra, catalogo: DefectoDeCatalogo[]): DefectoObservado[] {
	const porId = new Map(catalogo.map((d) => [d.id, d]));
	return Object.entries(b.defectos).flatMap(([id, grado]) => {
		const defecto = porId.get(id);
		return defecto ? [{ grupo: defecto.grupo, grado }] : [];
	});
}

/** Marcados en la hoja pero ausentes del catálogo (gestión lo sacó): no se pueden evaluar ni mandar. */
export function defectosDesconocidos(b: BorradorMuestra, catalogo: DefectoDeCatalogo[]): string[] {
	const conocidos = new Set(catalogo.map((d) => d.id));
	return Object.keys(b.defectos).filter((id) => !conocidos.has(id));
}

/** ¿Esta app sabe evaluar la regla? Una versión de algoritmo desconocida exige actualizar. */
export function reglaEvaluable(regla: ReglaCalidad | null): regla is ReglaCalidad {
	return regla !== null && ALGORITMOS_CONOCIDOS.includes(regla.algoritmo_version);
}

/** La propuesta de la regla, o `null` si todavía faltan las mediciones que el árbol mira. */
export function proponer(b: BorradorMuestra, regla: ReglaCalidad | null, catalogo: DefectoDeCatalogo[]): Propuesta | null {
	if (!reglaEvaluable(regla)) return null;
	const densidad = valorDe(b, 'densidad');
	// Sin humedad el árbol saltea su criterio (§12.9); con un texto que no es número, falta.
	const humedad = sinHumedad(b) ? null : valorDe(b, 'humedad');
	const polvo = valorDe(b, 'tamiz_bajo_40');
	if (densidad === null || (humedad === null && !sinHumedad(b)) || polvo === null || b.palos === null || b.contaminacion_grave === null) return null;
	return proponerLetra(regla, {
		densidad,
		humedad,
		tamiz_bajo_40: polvo,
		palos: b.palos,
		contaminacion_grave: b.contaminacion_grave,
		defectos: defectosObservados(b, catalogo),
	});
}

/** La letra que va a viajar: la fijada a mano, o la propuesta. */
export function letraAsignada(b: BorradorMuestra, propuesta: Propuesta | null): LetraCalidad | null {
	return b.letra_manual ?? propuesta?.letra ?? null;
}

/** ¿Hace falta motivo? Cuando la analista cambió la letra o la regla no decidió. */
export function motivoRequerido(b: BorradorMuestra, propuesta: Propuesta | null): boolean {
	const letra = letraAsignada(b, propuesta);
	return propuesta !== null && letra !== null && motivoObligatorio(propuesta, letra);
}

export function sumaTamices(b: BorradorMuestra, regla: ReglaCalidad | null): { completa: boolean; suma: number; fuera: boolean } {
	const valores = CAMPOS_TAMICES.map((c) => valorDe(b, c));
	const completa = valores.every((v) => v !== null);
	const suma = completa
		? sumaDeTamices({ tamiz_sobre_3_5: valores[0] as number, tamiz_sobre_6: valores[1] as number, tamiz_sobre_40: valores[2] as number, tamiz_bajo_40: valores[3] as number })
		: 0;
	const tolerancia = regla?.tolerancia_suma_tamices ?? null;
	return { completa, suma, fuera: completa && tolerancia !== null && Math.abs(suma - 100) > tolerancia };
}

export type EstadoHoja = { ok: true; texto: string } | { ok: false; texto: string };

/** Lo que dice el renglón de estado de la hoja, y si se puede guardar. Mismo orden que el boceto. */
export function estadoHoja(b: BorradorMuestra, regla: ReglaCalidad | null, catalogo: DefectoDeCatalogo[]): EstadoHoja {
	const faltan = faltantes(b);
	if (faltan.length > 0) return { ok: false, texto: `Faltan: ${faltan.join(', ')}.` };
	if (regla === null) return { ok: false, texto: 'Falta la regla de calidad: todavía no bajó de la oficina.' };
	if (!reglaEvaluable(regla)) return { ok: false, texto: 'La regla de calidad es más nueva que la app: hay que actualizarla.' };
	if (defectosDesconocidos(b, catalogo).length > 0) return { ok: false, texto: 'Un defecto marcado ya no está en el catálogo: destildalo.' };
	const propuesta = proponer(b, regla, catalogo);
	if (propuesta === null) return { ok: false, texto: 'Faltan mediciones.' };
	// La analista miró el resultado con otra regla: lo que vio (o fijó) puede no valer más. También
	// sin letra manual: la propuesta que dio por vista es la que tiene que viajar (Codex, 14/9).
	if (b.regla_version !== null && b.regla_version !== regla.version) return { ok: false, texto: 'La regla de calidad cambió: revisá el resultado.' };
	const letra = letraAsignada(b, propuesta);
	if (letra === null) return { ok: false, texto: 'Falta la letra: la regla no decide.' };
	if (motivoRequerido(b, propuesta) && b.motivo.trim() === '') return { ok: false, texto: 'Falta el motivo.' };
	const tamices = sumaTamices(b, regla);
	if (tamices.fuera) return { ok: true, texto: `Los tamices suman ${formatearNumero(tamices.suma)} g: tendrían que dar 100 ± ${regla.tolerancia_suma_tamices}. Se guarda igual.` };
	return { ok: true, texto: 'Todo cargado.' };
}

/** «12,5» para mostrar: la coma es la de la planilla. */
export function formatearNumero(n: number): string {
	return String(Math.round(n * 1000) / 1000).replace('.', ',');
}

/** El porqué de la propuesta, dicho para la analista. */
export function porqueDe(propuesta: Propuesta, regla: ReglaCalidad, b: BorradorMuestra): string {
	const polvo = valorDe(b, 'tamiz_bajo_40');
	switch (propuesta.rama) {
		case 'contaminacion_grave':
			return 'contaminación grave';
		case 'polvo_sobre_p_max':
			return `polvo ${formatearNumero(polvo ?? 0)} g > ${formatearNumero(regla.polvo_p_max)}`;
		case 'adventicios_requiere_decision':
			return regla.algoritmo_version === 1 ? 'hay adventicios: la regla no decide' : 'adventicios en bastante o mucho: la regla no decide';
		case 'adventicios':
			return 'adventicios';
		case 'materiales_extranios':
			return 'materiales extraños en bastante o mucho';
		case 'palos':
			return 'palos';
		case 'polvo_sobre_q_max':
			return `polvo ${formatearNumero(polvo ?? 0)} g > ${formatearNumero(regla.polvo_q_max)}`;
		case 'densidad':
			return `densidad fuera de ${formatearNumero(regla.densidad_min)}–${formatearNumero(regla.densidad_max)}`;
		case 'humedad':
			return `humedad fuera de ${formatearNumero(regla.humedad_min)}–${formatearNumero(regla.humedad_max)}`;
		default:
			return 'sin observaciones';
	}
}

// Un solo nombre por letra en las cuatro estaciones (2/10): la Q es «apta Virginia» como en el pallet.
export const NOMBRE_LETRA: Record<LetraCalidad, string> = { Q: 'Q · apta Virginia', P: 'P', L: 'L', descarte: 'Descarte' };

export class HojaIncompleta extends Error {}

function exigirNumero(b: BorradorMuestra, campo: CampoNumerico): number {
	if (!campoAceptable(b, campo)) throw new HojaIncompleta(`falta ${NOMBRE_CAMPO[campo]}`);
	return valorDe(b, campo) as number;
}

/**
 * El payload de `muestra.crear` (§5.2, matriz v4). Viaja `regla_version`: si la
 * regla cambió entre proponer y subir, el servidor rechaza con
 * `regla_desactualizada` y la hoja se vuelve a proponer con la nueva.
 */
export function armarPayloadMuestra(b: BorradorMuestra, regla: ReglaCalidad, catalogo: DefectoDeCatalogo[]): Record<string, unknown> {
	if (defectosDesconocidos(b, catalogo).length > 0) throw new HojaIncompleta('un defecto marcado ya no está en el catálogo');
	if (b.regla_version !== null && b.regla_version !== regla.version) throw new HojaIncompleta('la regla cambió: hay que revisar el resultado');
	const propuesta = proponer(b, regla, catalogo);
	if (propuesta === null) throw new HojaIncompleta('faltan mediciones');
	const letra = letraAsignada(b, propuesta);
	if (letra === null) throw new HojaIncompleta('falta la letra');
	if (b.palos === null || b.contaminacion_grave === null) throw new HojaIncompleta('faltan palos o contaminación');
	if (b.personas.length === 0) throw new HojaIncompleta('falta quién analiza');
	const motivo = b.motivo.trim().slice(0, MAX_TEXTO_MUESTRA);
	if (motivoRequerido(b, propuesta) && motivo === '') throw new HojaIncompleta('falta el motivo');
	return {
		muestra_id: b.muestra_id,
		llenado_id: b.llenado_id,
		tomada_en: instanteLocal(b.tomada_ms),
		persona_id: b.personas[0],
		densidad: exigirNumero(b, 'densidad'),
		humedad: sinHumedad(b) ? null : exigirNumero(b, 'humedad'),
		tamiz_sobre_3_5: exigirNumero(b, 'tamiz_sobre_3_5'),
		tamiz_sobre_6: exigirNumero(b, 'tamiz_sobre_6'),
		tamiz_sobre_40: exigirNumero(b, 'tamiz_sobre_40'),
		tamiz_bajo_40: exigirNumero(b, 'tamiz_bajo_40'),
		palos: b.palos,
		contaminacion_grave: b.contaminacion_grave,
		defectos: Object.entries(b.defectos).map(([defecto_id, grado]) => ({ defecto_id, grado })),
		defecto_otro: b.defecto_otro?.trim() ? b.defecto_otro.trim().slice(0, 100) : null,
		anexo_referido: null,
		regla_version: regla.version,
		letra_asignada: letra,
		motivo_decision: motivo === '' ? null : motivo,
		notas: b.nota?.trim() ? b.nota.trim().slice(0, MAX_TEXTO_MUESTRA) : null,
	};
}

/**
 * La hoja de vuelta a partir de lo que viajó y el servidor rechazó por
 * `regla_desactualizada` (§5.2.12): las mediciones se conservan, la letra y el
 * motivo se vuelven a decidir con la regla nueva, y viaja con identidad NUEVA —la
 * mutación rechazada no se reintenta con otro contenido (`id_reusado`).
 */
export function hojaDesdePayloadRechazado(
	payload: Record<string, unknown>,
	ids: { capture_id: string; mutacion_id: string; muestra_id: string },
	silo: Pick<BorradorMuestra, 'campania_id' | 'silo_numero' | 'precinto' | 'lote' | 'neto_g' | 'cerrado_en'>,
	ahoraMs: number,
): BorradorMuestra {
	// Tal cual viajó, sin redondear: «6,0001» es humedad válida y redondeada a «6» cambia la letra.
	const numero = (clave: string): string => (typeof payload[clave] === 'number' ? String(payload[clave]).replace('.', ',') : '');
	const siNo = (clave: string): SiNo | null => (payload[clave] === 0 || payload[clave] === 1 ? (payload[clave] as SiNo) : null);
	const defectos: Record<string, Grado> = {};
	for (const d of Array.isArray(payload.defectos) ? (payload.defectos as Array<{ defecto_id?: unknown; grado?: unknown }>) : []) {
		if (typeof d.defecto_id === 'string' && (d.grado === 'P' || d.grado === 'B' || d.grado === 'M')) defectos[d.defecto_id] = d.grado;
	}
	return {
		tipo: 'muestra',
		...ids,
		llenado_id: typeof payload.llenado_id === 'string' ? payload.llenado_id : ids.capture_id,
		...silo,
		personas: typeof payload.persona_id === 'string' ? [payload.persona_id] : [],
		densidad: numero('densidad'),
		humedad: numero('humedad'),
		tamiz_sobre_3_5: numero('tamiz_sobre_3_5'),
		tamiz_sobre_6: numero('tamiz_sobre_6'),
		tamiz_sobre_40: numero('tamiz_sobre_40'),
		tamiz_bajo_40: numero('tamiz_bajo_40'),
		palos: siNo('palos'),
		contaminacion_grave: siNo('contaminacion_grave'),
		defectos,
		defecto_otro: typeof payload.defecto_otro === 'string' ? payload.defecto_otro : null,
		letra_manual: null,
		motivo: '',
		nota: typeof payload.notas === 'string' ? payload.notas : null,
		pantalla: 'resultado',
		campo: null,
		tomada_ms: typeof payload.tomada_en === 'string' ? msDeInstanteLocal(payload.tomada_en) : ahoraMs,
		regla_version: null,
		actualizado_en: ahoraMs,
	};
}

// ── La lista de silos que esperan muestra ─────────────────────────────────

/**
 * El catálogo menos lo que este teléfono ya muestreó: las muestras pendientes en
 * la cola y las confirmadas que el snapshot todavía no refleja (misma mecánica
 * que los silos abiertos de producción, §5.1). Un silo con muestra cargada acá
 * no puede seguir ofrecido hasta el próximo refresco.
 */
export function silosParaMuestra(catalogo: SiloParaMuestra[], mutaciones: MutacionPendiente[]): SiloParaMuestra[] {
	const muestreados = silosConHumedadPropia(mutaciones);
	for (const m of mutaciones) {
		if (m.tipo !== 'muestra.crear') continue;
		const payload = m.payload as { llenado_id?: unknown } | null;
		if (payload && typeof payload.llenado_id === 'string') muestreados.add(payload.llenado_id);
	}
	return catalogo.filter((s) => !muestreados.has(s.llenado_id));
}

/**
 * §12.17: los silos con una versión de muestra de este teléfono que trae humedad (su tarjeta). El recibo de una
 * corrección pisa al de la hoja o al de completar la humedad, así que «Sin muestra» y «Falta humedad» no pueden
 * reconocerlos solo por el tipo: con un catálogo atrasado, el silo volvía a ofrecerse (Codex, ronda del diff, h. 2).
 */
function silosConHumedadPropia(mutaciones: Array<{ resumen?: unknown }>): Set<string> {
	const silos = new Set<string>();
	for (const m of mutaciones) {
		const tarjeta = tarjetaDe(m.resumen);
		if (tarjeta !== null) silos.add(tarjeta.llenado_id);
	}
	return silos;
}

// ── v6 (§12.9): la humedad que falta ─────────────────────────────────────

/** Una muestra vigente guardada sin humedad, tal como baja en `muestras_sin_humedad`. */
export interface MuestraSinHumedad {
	muestra_id: string;
	llenado_id: string;
	precinto: string;
	silo: number;
	nro_muestra: number;
	tomada_en: string;
	letra_asignada: LetraCalidad;
	letra_propuesta: LetraCalidad | null;
	motivo_decision: string | null;
	densidad: number;
	tamiz_bajo_40: number;
	palos: number;
	contaminacion_grave: number;
	/** JSON: `[{grupo, grado}]`. */
	defectos: string;
	/** §12.17: 1 si el precinto tiene envasados vigentes. Un catálogo de antes del 11/10 no lo trae. */
	con_bolsas?: number;
}

/** Las que esperan humedad: el catálogo menos las que este teléfono ya completó (en la cola o sin reflejar). */
export function muestrasSinHumedad(catalogo: MuestraSinHumedad[], mutaciones: Array<{ tipo: string; payload: unknown; resumen?: unknown }>): MuestraSinHumedad[] {
	const completadas = new Set<string>();
	for (const m of mutaciones) {
		if (m.tipo !== 'muestra.humedad') continue;
		const base = (m.payload as { corrige_a?: unknown } | null)?.corrige_a;
		if (typeof base === 'string') completadas.add(base);
	}
	const conHumedad = silosConHumedadPropia(mutaciones);
	return catalogo.filter((m) => !completadas.has(m.muestra_id) && !conHumedad.has(m.llenado_id));
}

/**
 * La letra con la humedad cargada, la misma regla que el servidor (§12.9): la que era la propuesta sin
 * humedad (sin motivo) pasa a la propuesta con ella; la decidida con motivo queda. `null` si la regla
 * no sabe evaluar.
 */
export function letraConHumedad(m: MuestraSinHumedad, humedad: number, regla: ReglaCalidad | null): LetraCalidad | null {
	if (m.motivo_decision !== null) return m.letra_asignada;
	if (!reglaEvaluable(regla)) return null;
	const defectos = (JSON.parse(m.defectos) as Array<{ grupo: 'material_extranio' | 'adventicio'; grado: 'P' | 'B' | 'M' }>).map((d) => ({ grupo: d.grupo, grado: d.grado }));
	const propuesta = proponerLetra(regla, {
		densidad: m.densidad,
		humedad,
		tamiz_bajo_40: m.tamiz_bajo_40,
		palos: m.palos === 1 ? 1 : 0,
		contaminacion_grave: m.contaminacion_grave === 1 ? 1 : 0,
		defectos,
	});
	return propuesta.letra ?? m.letra_asignada;
}

// ── §12.17: la humedad que se corrige ────────────────────────────────────

/** Una muestra con humedad, tal como baja en `humedades_recientes` (§12.17). */
export interface MuestraConHumedad extends Omit<MuestraSinHumedad, 'nro_muestra' | 'con_bolsas'> {
	/** `null` en una hoja de este teléfono que todavía no tiene acuse: el número lo pone el servidor. */
	nro_muestra: number | null;
	humedad: number;
	/** Cuándo entró esa humedad (ISO, la hora del servidor; en lo de este teléfono, la de la carga). */
	humedad_desde: string;
	/** Quién la cargó. */
	persona_id: string;
	con_bolsas: number;
}

/** Lo que baja el catálogo de calidad: el borde es del snapshot, nunca del reloj del teléfono. */
export interface HumedadesRecientes {
	desde: string;
	muestras: MuestraConHumedad[];
}

/**
 * Lo que la tarjeta necesita, guardado en el resumen de cada mutación que trae una humedad (la hoja con humedad,
 * completarla, corregirla). Viaja en la fila de la cola y en su recibo, así que la tarjeta se arma aunque el recibo
 * de una corrección pise al de la versión anterior y el catálogo no haya bajado (Codex, ronda del plan, h. 5).
 * Es el estado DESPUÉS de la mutación: la letra que queda y la persona que cargó.
 */
export type TarjetaDeHumedad = Omit<MuestraConHumedad, 'muestra_id' | 'humedad' | 'humedad_desde'> & { cargada_en: string };

/** Una muestra de la lista «Humedad cargada · últimas 24 h». */
export interface HumedadParaCorregir extends MuestraConHumedad {
	/** Sin reflejar todavía en el catálogo: en la cola o con acuse. */
	local: boolean;
	/** La mutación de este teléfono que trajo esta versión, mientras siga en la cola: el padre de la corrección. */
	mutacion_id?: string;
}

const TRAEN_HUMEDAD = new Set(['muestra.crear', 'muestra.humedad', 'muestra.corregir_humedad', 'muestra.corregir']);

function tarjetaDe(resumen: unknown): TarjetaDeHumedad | null {
	const r = resumen as { entidad?: unknown; tarjeta?: TarjetaDeHumedad } | null | undefined;
	return r?.entidad === 'muestra' && r.tarjeta ? r.tarjeta : null;
}

/**
 * La lista para corregir, proyectada por identidad (el silo: una muestra vigente por silo): el catálogo, y encima lo de
 * este teléfono que el snapshot todavía no refleja, en orden (recibos primero, después la cola). Lo que no se refleja
 * entra siempre: es posterior a la hora del snapshot, y el corte de 24 horas lo hace el catálogo que baja después.
 * Una anulación la saca. Lo más reciente arriba.
 */
export function humedadesParaCorregir(
	catalogo: HumedadesRecientes | null | undefined,
	mutaciones: Array<{ tipo: string; payload: unknown; mutacion_id?: string; resumen?: unknown; nro_muestra?: number | null }>,
): HumedadParaCorregir[] {
	const porSilo = new Map<string, HumedadParaCorregir>();
	for (const m of catalogo?.muestras ?? []) porSilo.set(m.llenado_id, { ...m, local: false });
	for (const m of mutaciones) {
		const p = m.payload as { muestra_id?: unknown; humedad?: unknown } | null;
		if (m.tipo === 'muestra.anular') {
			for (const [silo, h] of porSilo) if (h.muestra_id === p?.muestra_id) porSilo.delete(silo);
			continue;
		}
		if (!TRAEN_HUMEDAD.has(m.tipo)) continue;
		const tarjeta = tarjetaDe(m.resumen);
		// §12.18: una hoja corregida sin tarjeta (con contaminación grave, o con una humedad que ya pasó sus 48 horas) deja de
		// ofrecer su humedad: la de la lista apuntaba a la versión que se corrigió.
		const silo = (m.payload as { llenado_id?: unknown } | null)?.llenado_id;
		if (tarjeta === null && m.tipo === 'muestra.corregir' && typeof silo === 'string') porSilo.delete(silo);
		if (tarjeta === null || typeof p?.muestra_id !== 'string' || typeof p.humedad !== 'number') continue;
		const { cargada_en, ...resto } = tarjeta;
		porSilo.set(tarjeta.llenado_id, {
			...resto,
			// El número lo pone el servidor: llega en el acuse aunque la tarjeta se haya guardado sin él (h. 5).
			nro_muestra: resto.nro_muestra ?? m.nro_muestra ?? null,
			muestra_id: p.muestra_id,
			humedad: p.humedad,
			humedad_desde: cargada_en,
			local: true,
			...(m.mutacion_id === undefined ? {} : { mutacion_id: m.mutacion_id }),
		});
	}
	return [...porSilo.values()].sort((a, b) => b.humedad_desde.localeCompare(a.humedad_desde));
}

/**
 * La letra al corregir la humedad, la misma regla que el servidor con la regla del catálogo (que es la que viaja en
 * `regla_version`): la decidida con motivo queda; la que vino de la regla vuelve a salir de ella. Si con la regla de
 * hoy la propuesta queda sin decidir y la letra no tiene motivo, el teléfono no la corrige: el servidor respondería
 * `motivo_requerido`, y la letra la decide la oficina. `null` si la regla no sabe evaluar.
 */
export function letraAlCorregir(
	m: Pick<MuestraConHumedad, 'letra_asignada' | 'motivo_decision' | 'densidad' | 'tamiz_bajo_40' | 'palos' | 'contaminacion_grave' | 'defectos'>,
	humedad: number,
	regla: ReglaCalidad | null,
): { letra: LetraCalidad; decideLaOficina: boolean } | null {
	if (m.motivo_decision !== null) return { letra: m.letra_asignada, decideLaOficina: false };
	if (!reglaEvaluable(regla)) return null;
	const defectos = (JSON.parse(m.defectos) as Array<{ grupo: 'material_extranio' | 'adventicio'; grado: 'P' | 'B' | 'M' }>).map((d) => ({ grupo: d.grupo, grado: d.grado }));
	const propuesta = proponerLetra(regla, {
		densidad: m.densidad,
		humedad,
		tamiz_bajo_40: m.tamiz_bajo_40,
		palos: m.palos === 1 ? 1 : 0,
		contaminacion_grave: m.contaminacion_grave === 1 ? 1 : 0,
		defectos,
	});
	return propuesta.letra === null ? { letra: m.letra_asignada, decideLaOficina: true } : { letra: propuesta.letra, decideLaOficina: false };
}

/**
 * Cuánto se ofrece corregir una hoja o una humedad desde el teléfono: 24 horas para la humedad desde el 9/10 (Silvina),
 * 48 para todo desde el 15/10 (Agata: «48 hs para todos los datos», §12.18). El servidor lo hace cumplir (`pasaron_48_h`).
 */
export const VENTANA_DE_CALIDAD_MS = HORAS_PARA_CORREGIR * 60 * 60 * 1000;

/** Una versión de muestra, lo justo para seguir su cadena. */
export interface VersionDeHumedad {
	id: string;
	corrige_a: string | null;
	humedad: number | null;
	sincronizado_en: string;
	persona_id: string;
}

/**
 * La versión que trajo la humedad que la muestra tiene hoy: se sigue `corrige_a` hacia atrás mientras la humedad sea la
 * misma, por la cadena y no por la hora (dos versiones pueden compartir `sincronizado_en`). Un cambio de letra o una
 * corrección de la hoja con la misma humedad no la mueven; una corrección de la humedad, sí.
 */
export function versionQueTrajoLaHumedad(vigente: VersionDeHumedad, porId: ReadonlyMap<string, VersionDeHumedad>): VersionDeHumedad {
	let actual = vigente;
	const vistas = new Set<string>([vigente.id]);
	while (actual.corrige_a !== null && !vistas.has(actual.corrige_a)) {
		const base = porId.get(actual.corrige_a);
		if (base === undefined || base.humedad !== vigente.humedad) break;
		vistas.add(base.id);
		actual = base;
	}
	return actual;
}

// ── §12.18: la hoja que se ve y se corrige ───────────────────────────────

/** Un defecto de la hoja como baja en `hojas_recientes`: el id para el payload, el grupo para la regla. */
export interface DefectoDeLaHoja {
	defecto_id: string;
	grupo: DefectoDeCatalogo['grupo'];
	grado: Grado;
}

/**
 * Una hoja de las últimas 48 horas, tal como baja en `hojas_recientes` (§12.18): la versión vigente entera, con lo que
 * pide `validarMuestra` para corregirla; quién la cargó y cuándo llegó (el ancla de las 48 horas de la hoja); quién la
 * cambió después; y cuándo entró la humedad de hoy (el ancla de las 48 horas de la humedad).
 */
export interface HojaReciente {
	muestra_id: string;
	raiz_id: string;
	llenado_id: string;
	/** `null` en una hoja de este teléfono que todavía no tiene acuse: el número lo pone el servidor. */
	nro_muestra: number | null;
	silo: number;
	precinto: string;
	lote: string;
	/** El estado de la versión vigente del silo: `cerrado`, `descartado`… */
	silo_estado: string;
	tomada_en: string;
	densidad: number;
	humedad: number | null;
	tamiz_sobre_3_5: number;
	tamiz_sobre_6: number;
	tamiz_sobre_40: number;
	tamiz_bajo_40: number;
	palos: number;
	contaminacion_grave: number;
	defectos: DefectoDeLaHoja[];
	defecto_otro: string | null;
	anexo_referido: string | null;
	notas: string | null;
	letra_asignada: LetraCalidad;
	letra_propuesta: LetraCalidad | null;
	motivo_decision: string | null;
	/** La persona de envasado que decidió la letra (§12.5): la analista la respeta. */
	letra_por: string | null;
	/** La versión de la regla con que se evaluó. */
	regla_version: number | null;
	/** Quién cargó la hoja (la raíz) y cuándo llegó al servidor (en lo de este teléfono, cuándo se guardó). */
	cargada_por: string;
	hoja_desde: string;
	/** Si la vigente no es la raíz: quién la cambió, cuándo, y si fue desde la oficina. */
	cambiada_por: string | null;
	cambiada_en: string | null;
	desde_gestion: number;
	/** Cuándo entró la humedad de hoy y quién la cargó; `null` sin humedad. */
	humedad_desde: string | null;
	humedad_por: string | null;
	/** 1 si el precinto tiene envasados vigentes. */
	con_bolsas: number;
}

/** Lo que baja el catálogo de calidad: el borde es del snapshot, nunca del reloj del teléfono. */
export interface HojasRecientes {
	desde: string;
	hojas: HojaReciente[];
}

/**
 * Lo que guarda el resumen de un `muestra.crear` o un `muestra.corregir` de este teléfono: la hoja como queda, sin su id
 * (va en el payload). Así la lista la muestra sin señal y aunque el recibo de una versión pise al de la anterior.
 */
export type TarjetaDeHoja = Omit<HojaReciente, 'muestra_id'>;

/** Una hoja de la lista «Cargadas · últimas 48 h». */
export interface HojaParaCorregir extends HojaReciente {
	/** Sin reflejar todavía en el catálogo: en la cola o con acuse. */
	local: boolean;
	/** La mutación de este teléfono que trajo esta versión, mientras siga en la cola: el padre de la corrección. */
	mutacion_id?: string;
}

function hojaDe(resumen: unknown): TarjetaDeHoja | null {
	const r = resumen as { entidad?: unknown; hoja?: TarjetaDeHoja } | null | undefined;
	return r?.entidad === 'muestra' && r.hoja ? r.hoja : null;
}

/** Lo último que le pasó a la hoja: ordena la lista, lo más reciente arriba. */
function ultimoCambio(h: HojaReciente): string {
	return [h.hoja_desde, h.cambiada_en ?? '', h.humedad_desde ?? ''].reduce((a, b) => (b > a ? b : a));
}

/**
 * Las hojas para ver y corregir, proyectadas por identidad (el silo: una muestra vigente por silo), como
 * `humedadesParaCorregir`: el catálogo, y encima lo de este teléfono que el snapshot todavía no refleja, en orden (recibos
 * primero, después la cola). Una hoja o una corrección trae la hoja entera en su resumen; completar o corregir la humedad
 * trae solo su tarjeta, que mueve la vigente de una hoja que ya está en la lista. Una anulación la saca.
 */
export function hojasParaCorregir(
	catalogo: HojasRecientes | null | undefined,
	mutaciones: Array<{ tipo: string; payload: unknown; mutacion_id?: string; resumen?: unknown; nro_muestra?: number | null }>,
): HojaParaCorregir[] {
	const porSilo = new Map<string, HojaParaCorregir>();
	for (const h of catalogo?.hojas ?? []) porSilo.set(h.llenado_id, { ...h, local: false });
	for (const m of mutaciones) {
		const p = m.payload as { muestra_id?: unknown; humedad?: unknown } | null;
		if (m.tipo === 'muestra.anular') {
			for (const [silo, h] of porSilo) if (h.muestra_id === p?.muestra_id) porSilo.delete(silo);
			continue;
		}
		if (!TRAEN_HUMEDAD.has(m.tipo) || typeof p?.muestra_id !== 'string') continue;
		const deLaCola = m.mutacion_id === undefined ? {} : { mutacion_id: m.mutacion_id };
		const hoja = hojaDe(m.resumen);
		if (hoja !== null) {
			porSilo.set(hoja.llenado_id, { ...hoja, nro_muestra: hoja.nro_muestra ?? m.nro_muestra ?? null, muestra_id: p.muestra_id, local: true, ...deLaCola });
			continue;
		}
		const tarjeta = tarjetaDe(m.resumen);
		const previa = tarjeta === null ? undefined : porSilo.get(tarjeta.llenado_id);
		if (tarjeta === null || previa === undefined || typeof p.humedad !== 'number') continue;
		porSilo.set(tarjeta.llenado_id, {
			...previa,
			muestra_id: p.muestra_id,
			humedad: p.humedad,
			humedad_desde: tarjeta.cargada_en,
			humedad_por: tarjeta.persona_id,
			letra_asignada: tarjeta.letra_asignada,
			letra_propuesta: tarjeta.letra_propuesta,
			cambiada_por: tarjeta.persona_id,
			cambiada_en: tarjeta.cargada_en,
			desde_gestion: 0,
			local: true,
			...deLaCola,
		});
	}
	return [...porSilo.values()].sort((a, b) => ultimoCambio(b).localeCompare(ultimoCambio(a)));
}

/** Una tarjeta de «Cargadas · últimas 48 h»: la hoja, la humedad corregible, o las dos, del mismo silo. */
export interface Cargada {
	llenado_id: string;
	hoja: HojaParaCorregir | null;
	humedad: HumedadParaCorregir | null;
}

/**
 * La lista «Cargadas · últimas 48 h» (§12.18): las hojas y las humedades por silo. Una humedad sin hoja es la de un catálogo
 * de antes del 15/10, o una completada sin señal sobre una hoja que no está en la lista: su tarjeta va directo a corregirla.
 */
export function cargadasRecientes(hojas: HojaParaCorregir[], humedades: HumedadParaCorregir[]): Cargada[] {
	const porSilo = new Map<string, Cargada>();
	for (const h of hojas) porSilo.set(h.llenado_id, { llenado_id: h.llenado_id, hoja: h, humedad: null });
	for (const m of humedades) {
		const previa = porSilo.get(m.llenado_id);
		porSilo.set(m.llenado_id, { llenado_id: m.llenado_id, hoja: previa?.hoja ?? null, humedad: m });
	}
	const cuando = (c: Cargada) => (c.hoja === null ? (c.humedad?.humedad_desde ?? '') : ultimoCambio(c.hoja));
	return [...porSilo.values()].sort((a, b) => cuando(b).localeCompare(cuando(a)));
}

/**
 * ¿La hoja se corrige entera desde el teléfono? Si llegó dentro de las 48 horas antes del snapshot (`desde`, del
 * catálogo; corregirla no las renueva). Una hoja vieja que entró a la lista por una humedad reciente se ve, pero solo se
 * corrige su humedad (§12.18, «cada botón mira su propio ancla»). Sin borde, solo lo de este teléfono.
 */
export function hojaCorregible(h: HojaParaCorregir, desde: string | null | undefined): boolean {
	if (!desde) return h.local;
	return Date.parse(h.hoja_desde) >= Date.parse(desde);
}

/**
 * El borrador de «Corregir la hoja»: la misma hoja de siempre, prellenada con la base, en el resumen. La letra con motivo
 * (o la que decidió envasado) entra fijada con su motivo; la que proponía la regla vuelve a salir de ella.
 */
export function hojaParaCorregir(
	h: HojaParaCorregir,
	ids: { capture_id: string; mutacion_id: string; muestra_id: string },
	personas: string[],
	ahoraMs: number,
): BorradorMuestra {
	const b = hojaDesdePayloadRechazado(
		{ ...h, persona_id: undefined, defectos: h.defectos.map(({ defecto_id, grado }) => ({ defecto_id, grado })) },
		ids,
		{ campania_id: '', silo_numero: String(h.silo), precinto: h.precinto, lote: h.lote, neto_g: 0, cerrado_en: '' },
		ahoraMs,
	);
	const fijada = h.motivo_decision !== null || h.letra_por !== null;
	return { ...b, personas, pantalla: 'hoja', letra_manual: fijada ? h.letra_asignada : null, motivo: h.motivo_decision ?? '', corrige: h };
}

/** La letra que queda con la corrección: la de envasado no se toca; si no, la de la hoja corregida. */
export function letraDeLaCorreccion(b: BorradorMuestra, regla: ReglaCalidad | null, catalogo: DefectoDeCatalogo[]): LetraCalidad | null {
	if (b.corrige?.letra_por) return b.corrige.letra_asignada;
	return letraAsignada(b, proponer(b, regla, catalogo));
}

/**
 * El payload del `muestra.corregir` de la analista (§12.18): la hoja corregida sobre la base. La toma, el anexo y las
 * notas de la muestra son los de la base (`armarPayloadMuestra` fija el anexo en null). La letra de envasado viaja tal
 * cual con su motivo —el servidor rechaza otra con `letra_de_envasado`—; la de la analista lleva motivo solo si hace falta.
 */
export function armarPayloadCorreccion(b: BorradorMuestra, regla: ReglaCalidad, catalogo: DefectoDeCatalogo[]): Record<string, unknown> {
	const base = b.corrige;
	if (base === undefined) throw new HojaIncompleta('no es una corrección');
	const payload = armarPayloadMuestra(b, regla, catalogo);
	const letra = base.letra_por
		? { letra_asignada: base.letra_asignada, motivo_decision: base.motivo_decision }
		: { motivo_decision: motivoRequerido(b, proponer(b, regla, catalogo)) ? payload.motivo_decision : null };
	return { ...payload, ...letra, corrige_a: base.muestra_id, tomada_en: base.tomada_en, anexo_referido: base.anexo_referido, notas: base.notas };
}

const GRADO_TEXTO: Record<Grado, string> = { P: 'poco', B: 'bastante', M: 'mucho' };
const siNoTexto = (v: number | null) => (v === null ? '—' : v === 1 ? 'sí' : 'no');
const numeroTexto = (v: number | null) => (v === null ? '—' : formatearNumero(v));

/** Los defectos de una hoja guardada, por nombre y grado: «amarillo poco, piedras bastante», o «ninguno». */
export function defectosTexto(defectos: Array<{ defecto_id: string; grado: Grado }>, grupo: DefectoDeCatalogo['grupo'], catalogo: DefectoDeCatalogo[]): string {
	const porId = new Map(catalogo.map((d) => [d.id, d]));
	const delGrupo = defectos
		.filter((d) => porId.get(d.defecto_id)?.grupo === grupo)
		.sort((a, b) => (porId.get(a.defecto_id)?.orden ?? 0) - (porId.get(b.defecto_id)?.orden ?? 0))
		.map((d) => `${porId.get(d.defecto_id)?.nombre ?? d.defecto_id} ${GRADO_TEXTO[d.grado]}`);
	return delGrupo.length === 0 ? 'ninguno' : delGrupo.join(', ');
}

/** Lo que cambió entre la base y la hoja corregida, en palabras: «densidad 22 → 23», «palos no → sí», «amarillo — → poco». */
export function cambiosDeLaHoja(base: HojaReciente, b: BorradorMuestra, catalogo: DefectoDeCatalogo[]): string[] {
	const cambios: string[] = [];
	const numeros: Array<[CampoNumerico, string]> = [
		['densidad', 'densidad'],
		['humedad', 'humedad'],
		['tamiz_sobre_3_5', 'sobre 3,5'],
		['tamiz_sobre_6', 'sobre 6'],
		['tamiz_sobre_40', 'sobre 40'],
		['tamiz_bajo_40', 'polvo'],
	];
	for (const [campo, nombre] of numeros) {
		const ahora = campo === 'humedad' && sinHumedad(b) ? null : valorDe(b, campo);
		if (ahora !== base[campo]) cambios.push(`${nombre} ${numeroTexto(base[campo])} → ${numeroTexto(ahora)}`);
	}
	if (b.palos !== base.palos) cambios.push(`palos ${siNoTexto(base.palos)} → ${siNoTexto(b.palos)}`);
	if (b.contaminacion_grave !== base.contaminacion_grave) cambios.push(`contaminación grave ${siNoTexto(base.contaminacion_grave)} → ${siNoTexto(b.contaminacion_grave)}`);
	const antes = new Map(base.defectos.map((d) => [d.defecto_id, d.grado]));
	const nombre = new Map(catalogo.map((d) => [d.id, d.nombre]));
	for (const id of new Set([...antes.keys(), ...Object.keys(b.defectos)])) {
		const de = antes.get(id);
		const a = b.defectos[id];
		if (de !== a) cambios.push(`${nombre.get(id) ?? id} ${de ? GRADO_TEXTO[de] : '—'} → ${a ? GRADO_TEXTO[a] : '—'}`);
	}
	const otroAntes = base.defecto_otro?.trim() || null;
	const otroAhora = b.defecto_otro?.trim() || null;
	if (otroAntes !== otroAhora) cambios.push(`otro material ${otroAntes ?? '—'} → ${otroAhora ?? '—'}`);
	return cambios;
}

/**
 * Lo que dice el renglón de estado de la corrección y si se guarda. Antes que la hoja: la contaminación grave que entra
 * con bolsas (el servidor la rechaza, `contaminacion_con_bolsas`), la humedad que se borra (`humedad_cargada`) y una
 * corrección sin cambios. Con la letra de envasado no hay resultado que revisar.
 */
export function estadoCorreccion(b: BorradorMuestra, regla: ReglaCalidad | null, catalogo: DefectoDeCatalogo[]): EstadoHoja {
	const base = b.corrige;
	if (base === undefined) return estadoHoja(b, regla, catalogo);
	if (b.contaminacion_grave === 1 && base.contaminacion_grave === 0 && base.con_bolsas === 1) {
		return { ok: false, texto: 'Con bolsas, la contaminación grave la carga la oficina: descartaría un silo ya embolsado.' };
	}
	if (base.humedad !== null && sinHumedad(b)) return { ok: false, texto: 'La humedad ya estaba cargada: no se puede dejar vacía.' };
	const letra = letraDeLaCorreccion(b, regla, catalogo);
	const motivo = b.motivo.trim() === '' ? null : b.motivo.trim();
	const letraCambia = letra !== base.letra_asignada || (base.letra_por === null && b.letra_manual !== null && motivo !== base.motivo_decision);
	if (cambiosDeLaHoja(base, b, catalogo).length === 0 && !letraCambia) return { ok: false, texto: 'Todavía no cambiaste nada: tocá una fila para cambiarla.' };
	const estado = estadoHoja(b, regla, catalogo);
	if (!estado.ok) return estado;
	if (base.letra_por) return { ok: true, texto: `La letra la decidió envasado: queda ${NOMBRE_LETRA[base.letra_asignada]}.` };
	return estado.texto === 'Todo cargado.' ? { ok: true, texto: 'Lista para guardar la corrección.' } : estado;
}

/**
 * La hoja como queda después de guardar `payload` (un `muestra.crear` o un `muestra.corregir` de este teléfono), para el
 * resumen de la mutación: las mediciones del payload, y lo que el payload no trae —quién la cargó, cuándo, el silo— de `datos`.
 */
export function hojaDelPayload(
	payload: Record<string, unknown>,
	catalogo: DefectoDeCatalogo[],
	datos: Omit<TarjetaDeHoja, keyof PayloadDeLaHoja | 'defectos' | 'regla_version'>,
): TarjetaDeHoja {
	const grupo = new Map(catalogo.map((d) => [d.id, d.grupo]));
	const p = payload as unknown as PayloadDeLaHoja & { defectos: Array<{ defecto_id: string; grado: Grado }>; regla_version: number };
	return {
		...datos,
		tomada_en: p.tomada_en,
		densidad: p.densidad,
		humedad: p.humedad,
		tamiz_sobre_3_5: p.tamiz_sobre_3_5,
		tamiz_sobre_6: p.tamiz_sobre_6,
		tamiz_sobre_40: p.tamiz_sobre_40,
		tamiz_bajo_40: p.tamiz_bajo_40,
		palos: p.palos,
		contaminacion_grave: p.contaminacion_grave,
		defectos: p.defectos.flatMap((d) => {
			const g = grupo.get(d.defecto_id);
			return g === undefined ? [] : [{ defecto_id: d.defecto_id, grupo: g, grado: d.grado }];
		}),
		defecto_otro: p.defecto_otro,
		anexo_referido: p.anexo_referido,
		notas: p.notas,
		letra_asignada: p.letra_asignada,
		motivo_decision: p.motivo_decision,
		regla_version: p.regla_version,
	};
}

/** Los campos de la hoja que viajan en el payload tal como quedan en la muestra. */
type PayloadDeLaHoja = Pick<
	HojaReciente,
	| 'tomada_en'
	| 'densidad'
	| 'humedad'
	| 'tamiz_sobre_3_5'
	| 'tamiz_sobre_6'
	| 'tamiz_sobre_40'
	| 'tamiz_bajo_40'
	| 'palos'
	| 'contaminacion_grave'
	| 'defecto_otro'
	| 'anexo_referido'
	| 'notas'
	| 'letra_asignada'
	| 'motivo_decision'
>;
